import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Link,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  useDisplayContactFooter,
  useHelpCreateTicketEmail,
  useHelpDeskHours,
  useHelpDeskPhoneNumber,
  useHelpDocumentationURL,
  useHelpFAQURL,
} from "@/hooks/useSetting";

const HelpAndSupport = () => {
  const helpDeskPhoneNumber = useHelpDeskPhoneNumber();
  const helpFAQURL = useHelpFAQURL();
  const helpDeskHours = useHelpDeskHours();
  const helpDocumentationURL = useHelpDocumentationURL();
  const helpCreateTicketEmail = useHelpCreateTicketEmail();
  const displayContactFooter = useDisplayContactFooter();

  const handleDownload = (url: string, filename?: string) => {
    const link = document.createElement("a");
    link.href = url;
    if (filename) {
      link.download = filename;
    }
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreateTicket = () => {
    const subject = "";
    const mailto = `mailto:${helpCreateTicketEmail}?subject=${encodeURIComponent(
      subject || "",
    )}`;
    window.location.href = mailto;
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Help & support
      </Typography>

      <Grid container spacing={3}>
        <Grid container size={{ xs: 12, md: 4 }}>
          <Card
            elevation={2}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: "400px",
            }}
          >
            <CardContent>
              <Box
                component="img"
                src="/assets/FAQ-Illustration.png"
                alt="FAQ"
                sx={{ width: "100%", height: 160, objectFit: "contain", mb: 2 }}
              />
              <Typography variant="subtitle1" fontWeight="bold">
                Frequently asked questions
              </Typography>
              <Typography variant="body2" mt={1} sx={{ color: "#797D7D" }}>
                Download the Frequently Asked Questions which has information
                about how to manage your Beneficiaries and distribute
                incentives.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  if (helpFAQURL) {
                    handleDownload(helpFAQURL, "faq.pdf");
                  }
                }}
              >
                DOWNLOAD
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid container size={{ xs: 12, md: 4 }}>
          <Card
            elevation={2}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <CardContent>
              <Box
                component="img"
                src="/assets/training-material.png"
                alt="Manual"
                sx={{ width: "100%", height: 160, objectFit: "contain", mb: 2 }}
              />
              <Typography variant="subtitle1" fontWeight="bold">
                Training materials & program manual
              </Typography>
              <Typography variant="body2" mt={1} sx={{ color: "#797D7D" }}>
                To access a complete list of training materials and the program
                manual, please click the button below.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  if (helpDocumentationURL) {
                    handleDownload(helpDocumentationURL, "faq.pdf");
                  }
                }}
              >
                DOWNLOAD
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid container size={{ xs: 12, md: 4 }}>
          <Card
            elevation={2}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <CardContent>
              <Box
                component="img"
                src="/assets/helpdesk-Illustration.png"
                alt="Create Ticket"
                sx={{ width: "100%", height: 160, objectFit: "contain", mb: 2 }}
              />
              <Typography variant="subtitle1" fontWeight="bold">
                Create ticket
              </Typography>
              <Typography variant="body2" mt={1} sx={{ color: "#797D7D" }}>
                Generate a ticket, and our dedicated support staff will promptly
                assist you in resolving any problems, ensuring a smooth and
                hassle-free experience.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                fullWidth
                variant="contained"
                onClick={handleCreateTicket}
              >
                CREATE REQUEST
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {displayContactFooter && (
        <Box
          mt={2}
          p={3}
          border="1px solid"
          borderColor="divider"
          borderRadius={2}
          sx={{
            backgroundColor: "white",
          }}
        >
          <Typography fontWeight="bold" gutterBottom>
            Still need help? Contact us
          </Typography>
          <Typography variant="body2" mb={1} sx={{ color: "#797D7D" }}>
            If you're in need of additional support, please call the CMI help
            desk at:
          </Typography>
          <Typography variant="body2" mb={1} sx={{ color: "#797D7D" }}>
            {helpDeskHours}
          </Typography>
          <Link href={`tel:${helpDeskPhoneNumber}`} sx={{ color: "#7034E7" }}>
            {helpDeskPhoneNumber}
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default HelpAndSupport;
